"use client";

/**
 * Client body for the change-validation page (/product/change-validation).
 * Copy lives in the `changeValidation` namespace; structure stays here.
 *
 * Claims posture: isolated multi-container validation is pilot / per-tenant
 * opt-in; customer-environment validation is policy-driven pilot through the
 * FC Agent. The page never suggests FixControl replaces the customer's CI/CD.
 */

import {
  Boxes,
  Container,
  FileCheck2,
  GitPullRequest,
  History,
  Rocket,
  ShieldCheck,
  SlidersHorizontal,
  Wrench,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  SectionHeader,
  WorkflowSteps,
  FeatureGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useLocale, useT } from "@/i18n";

export function ChangeValidationContent() {
  const t = useT();
  const { locale } = useLocale();
  const k = (s: string) => `changeValidation.${s}`;

  const steps = [
    { key: "step1", icon: <Wrench size={18} /> },
    { key: "step2", icon: <Container size={18} /> },
    { key: "step3", icon: <FileCheck2 size={18} /> },
    { key: "step4", icon: <ShieldCheck size={18} /> },
    { key: "step5", icon: <GitPullRequest size={18} /> },
    { key: "step6", icon: <Rocket size={18} /> },
  ].map((s) => ({
    icon: s.icon,
    tag: t(k(`flow.${s.key}.tag`)),
    title: t(k(`flow.${s.key}.title`)),
    body: t(k(`flow.${s.key}.body`)),
  }));

  const modes = [
    { key: "local", icon: <Boxes size={18} /> },
    { key: "remote", icon: <Container size={18} /> },
  ].map((m) => ({
    icon: m.icon,
    title: t(k(`modes.${m.key}.title`)),
    body: t(k(`modes.${m.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/integrations/fc-agent", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section>
        <SectionHeader eyebrow={t(k("flow.eyebrow"))} title={t(k("flow.title"))} />
        <WorkflowSteps steps={steps} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("modes.eyebrow"))}
          title={t(k("modes.title"))}
          description={t(k("modes.description"))}
        />
        <FeatureGrid features={modes} columns={2} />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("policy.eyebrow"))} title={t(k("policy.title"))} />
        <SecurityNote icon={<SlidersHorizontal size={18} />} title={t(k("policy.noteTitle"))}>
          {t(k("policy.noteBody"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("failure.eyebrow"))} title={t(k("failure.title"))} />
        <SecurityNote icon={<History size={18} />} title={t(k("failure.noteTitle"))}>
          {t(k("failure.noteBody"))}
        </SecurityNote>
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section muted>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/integrations/fc-agent",
              label: t(k("related.agent.label")),
              description: t(k("related.agent.description")),
            },
            {
              href: "/use-cases/ai-deployment-governance",
              label: t(k("related.deployment.label")),
              description: t(k("related.deployment.description")),
            },
            {
              href: "/security",
              label: t(k("related.security.label")),
              description: t(k("related.security.description")),
            },
            {
              href: `/docs/${locale}/admin/validation`,
              label: t(k("related.docs.label")),
              description: t(k("related.docs.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
